import {CommonModule} from '@angular/common';
import {Component, computed, resource, Resource, signal, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTableModule} from '@angular/material/table';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {pdcRoutes} from '@app/routing/routes';
import {FileResponse as PublicDatasetFileResponse, PublicDatasetFileService} from '@middleware';
import {CommonsSearchDataset} from '@models/commons-types';
import {
  KeyValueTableComponent,
  keyValueTableObject,
} from '@shared/components/key-value-table/key-value-table.component';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {formatSize} from '@utilities/format-size';
import {lastValueFrom, map} from 'rxjs';

@Component({
  selector: 'app-dataset-view-public',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTableModule, KeyValueTableComponent],
  templateUrl: './dataset-view-public.html',
})
export class DatasetViewPublic implements SectionHeaderActions {
  public dataset: Signal<CommonsSearchDataset>;
  public downloadLink: Resource<PublicDatasetFileResponse>;
  public readonly activeFileVersionsCount = computed(() => {
    console.log(
      this.dataset()?.dataset_file_versions?.filter(version => version.is_latest_and_not_deleted).length ?? 0,
    );
    return this.dataset()?.dataset_file_versions?.filter(version => version.is_latest_and_not_deleted).length ?? 0;
  });
  constructor(
    activatedRoute: ActivatedRoute,
    private readonly sanitizer: DomSanitizer,
    private readonly publicDatasetFileService: PublicDatasetFileService,
    private readonly router: Router,
  ) {
    this.dataset = toSignal(activatedRoute.data.pipe(map(data => data['dataset'])));
    this.downloadLink = resource({
      params: () => ({dataset: this.dataset()?.id}),
      loader: ({params}) => {
        if (params.dataset)
          return lastValueFrom(
            this.publicDatasetFileService
              .getPublicDatasetFile(params.dataset)
              .pipe(map(pdfMetadata => pdfMetadata.file)),
          );
      },
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/public-data-commons/view-public-dataset',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  public readonly safeDescription = computed<SafeHtml>(() => {
    const description = this.dataset()?.description;
    if (!description) return '';

    // Bypass Angular's sanitizer to allow this specific piece of HTML to be WYSIWYG
    return this.sanitizer.bypassSecurityTrustHtml(description); // nosonar
  });

  public formatFileSize(bytes: number): string {
    return formatSize(bytes);
  }

  public readonly buildKeyValueTable = computed<keyValueTableObject>(() => {
    const dataset = this.dataset();
    if (!dataset) return [];
    const tableData: keyValueTableObject = [];
    const primary_project = dataset.associated_projects.find(project => project.is_primary_project);
    const other_projects = dataset.associated_projects.filter(project => !project.is_primary_project);
    other_projects.sort((a, b) => a.project.name.localeCompare(b.project.name));

    tableData.push(
      {key: 'dataset_type', display_name: 'Dataset Type', value: dataset.dataset_type},
      {key: 'keywords', display_name: 'Keywords', value: dataset.keywords?.join(', ')},
      {
        key: 'primary_project',
        display_name: 'Primary Associated Project',
        value: primary_project.project.name,
        link: '/' + pdcRoutes.projectView(primary_project.project.id).slice(1).join('/'),
      },
      {
        key: 'other_projects',
        display_name: 'Other Associated Projects',
        value: other_projects.map(project => project.project.name),
      },
      {
        key: 'storage_organization',
        display_name: 'Storage Organization',
        value: dataset.storage_organization?.full_name,
      },
      {
        key: 'identifiers_hipaa',
        display_name: 'HIPAA Identifiers',
        value: dataset.identifiers_hipaa?.map(hipaa => hipaa.text),
      },
      {key: 'study_irb_number', display_name: 'IRB Study Number', value: dataset.study_irb_number},
      {key: 'data_aggregation', display_name: 'Data Granularity', value: dataset.data_aggregation?.text},
      {
        key: 'other_sensitive_data',
        display_name: 'Other Sensitive Data',
        value: dataset.other_sensitive_data?.map(osd => osd.text),
      },
      {key: 'license', display_name: 'License', value: dataset.license?.text, link: dataset.license?.url},
      {
        key: 'doi',
        display_name: 'Document Object Identifier (DOI)',
        value: dataset.doi,
        link: dataset.doi?.startsWith('doi:') ? 'https://doi.org/' + dataset.doi.substring(4) : undefined,
      },
      {key: 'attribution', display_name: 'Attribution', value: dataset.attribution},
      {
        key: 'link_to_external_dataset',
        display_name: 'Link to External Dataset File',
        value: dataset.link_to_external_dataset,
        link: dataset.link_to_external_dataset,
      },
    );
    if (dataset.dataset_type == 'REDCap') {
      tableData.push({
        key: 'redcap_extract_data',
        display_name: 'Extract from REDCap',
        value: this.getREDCapExtractPlanName(dataset),
      });
    }
    if (dataset.dataset_type == 'DICOM') {
      tableData.push(
        {
          key: 'bids_structure',
          display_name: 'Brain Imaging Data Structure (BIDS)',
          value: dataset.dicom_metadata.bids_structure ? 'Yes' : 'No',
        },
        {
          key: 'quality',
          display_name: 'Quality',
          value: dataset.dicom_metadata.quality[0].toUpperCase() + dataset.dicom_metadata.quality.substring(1),
        },
      );
      dataset.dicom_metadata.de_identified = undefined;
    } else {
      tableData.push({
        key: 'variables_measured',
        display_name: 'Variables Measured',
        value: dataset.variables_measured.join(', '),
      });
    }
    return tableData;
  });

  private getREDCapExtractPlanName(dataset: any) {
    if (!dataset.redcap_metadata.redcap_extract_data) return 'None';
    if (dataset.redcap_metadata.is_data_dictionary) return 'Data Dictionary';
    if (!dataset.redcap_metadata.redcap_report_id) return 'All Data';
    return 'Single Report';
  }

  public readonly buildDicomTable = computed<keyValueTableObject>(() => {
    const dataset = this.dataset();
    if (dataset?.dataset_type != 'DICOM') return [];
    const labels = {
      acquisition_date: 'Acquisition Date',
      angio_flag: 'Angio Flag',
      bids_structure: 'Brain Imaging Data Structure (BIDS)',
      de_identified: 'De-identified',
      field_of_view: 'Field of View',
      field_strength: 'Field Strength',
      institution_name: 'Imaging Institution Name',
      largest_image_pixel_value: 'Largest Image Pixel Value',
      modality: 'Modality',
      mr_acquisition_type: 'MR Acquisition Type',
      organ_name: 'Organ Name',
      patient_sex: 'Patient Gender',
      protocol_name: 'Protocol Name',
      quality: 'Quality',
      scanner_manufacturer_name: 'Scanner Manufacturer',
      scanner_model_name: 'Scanner Model',
      software_versions: 'Software Versions',
      study_date: 'Study Date',
    };
    return Object.keys(dataset.dicom_metadata)
      .filter(k => k != 'bids_structure' && k != 'quality')
      .map(key => ({
        key,
        display_name: labels[key] ?? key,
        value:
          typeof dataset.dicom_metadata[key] == 'boolean'
            ? dataset.dicom_metadata[key]
              ? 'Yes'
              : 'No'
            : dataset.dicom_metadata[key],
      }));
  });

  public get_file_size(file: any) {
    return formatSize(file.file_size);
  }
}
